import React, { useEffect, useState } from 'react';
import {
  backgroundColorOptions,
  textColorOptions
} from './theme/colors';

// Official Puck Header component implementation
const HeaderComponent = ({
  logoText = 'Your Brand',
  logoUrl = '',
  navigation = [
    { text: 'Home', url: '#home' },
    { text: 'About', url: '#about' },
    { text: 'Contact', url: '#contact' }
  ],
  backgroundColor = 'white',
  textColor = 'gray-900',
  logoColor = 'blue-600',
  padding = 'md',
  sticky = false,
  showLogo = true,
  showNavigation = true,
  showThemeToggle = false,
  align = 'center'
}) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Removed color resolution - now using Tailwind dark mode classes

  const getPaddingClass = () => {
    switch (padding) {
      case 'sm':
        return 'py-3 px-4';
      case 'md':
        return 'py-4 px-6';
      case 'lg':
        return 'py-6 px-8';
      case 'xl':
        return 'py-8 px-12';
      default:
        return 'py-4 px-6';
    }
  };

  const getAlignClass = () => {
    switch (align) {
      case 'left':
        return 'justify-start';
      case 'center':
        return 'justify-center';
      case 'right':
        return 'justify-end';
      case 'between':
        return 'justify-between';
      default:
        return 'justify-center';
    }
  };

  const getStickyClass = () => (sticky ? 'sticky top-0 z-50 backdrop-blur' : '');
  const getShadowClass = () => (sticky ? 'shadow-xs' : '');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;
    const storedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldEnable = storedTheme ? storedTheme === 'dark' : prefersDark;
    setIsDarkMode(shouldEnable);
    root.classList.toggle('dark', shouldEnable);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.classList.toggle('dark', isDarkMode);
    try {
      localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    } catch (error) {
      // ignore storage issues
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  const renderThemeToggle = () => {
    if (!showThemeToggle) return null;
    return (
      <button
        type="button"
        onClick={toggleTheme}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-current/20 bg-white/10 text-current transition-colors hover:bg-white/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        aria-label={isDarkMode ? 'Hellen Modus aktivieren' : 'Dunklen Modus aktivieren'}
      >
        {isDarkMode ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 3v1.5m0 15V21m9-9h-1.5M4.5 12H3m15.364 6.364l-1.06-1.06M6.696 6.696l-1.06-1.06m12.728 0l-1.06 1.06M6.696 17.304l-1.06 1.06M15.5 12a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <header
      className={`w-full bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 ${getStickyClass()} ${getShadowClass()}`}
    >
      <div className={`max-w-7xl mx-auto ${getPaddingClass()}`}>
        <div className={`flex items-center gap-6 w-full ${getAlignClass()}`}>
          {showLogo && (
            <div className="shrink-0">
              {logoUrl ? (
                <a href={logoUrl} className="block">
                  <img
                    src={logoUrl}
                    alt={logoText}
                    className="h-8 w-auto"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      if (e.currentTarget.nextSibling) {
                        e.currentTarget.nextSibling.style.display = 'block';
                      }
                    }}
                  />
                  <span className="text-xl font-bold text-blue-600 dark:text-blue-400 hidden">
                    {logoText}
                  </span>
                </a>
              ) : (
                <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                  {logoText}
                </span>
              )}
            </div>
          )}

          {align === 'between' && <div className="flex-1" />}

          {(showNavigation || showThemeToggle) && (
            <div
              className={`flex items-center gap-6 ${showLogo && align !== 'between' ? 'ml-8' : ''} ${align === 'center' ? 'mx-auto' : ''}`}
            >
              {showNavigation && navigation && navigation.length > 0 && (
                <nav className="flex items-center gap-8">
                  {navigation.map((item, index) => (
                    <a
                      key={index}
                      href={item.url || '#'}
                      className="text-sm font-medium transition-colors duration-200 hover:opacity-80 text-gray-900 dark:text-slate-100"
                    >
                      {item.text}
                    </a>
                  ))}
                </nav>
              )}

              {renderThemeToggle()}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export const Header = {
  fields: {
    logoText: {
      type: 'text',
      label: 'Logo Text'
    },
    logoUrl: {
      type: 'text',
      label: 'Logo Bild URL',
      placeholder: 'https://example.com/logo.png'
    },
    navigation: {
      type: 'array',
      label: 'Navigation',
      arrayFields: {
        text: {
          type: 'text',
          label: 'Link Text'
        },
        url: {
          type: 'text',
          label: 'Link URL',
          placeholder: '#home oder https://example.com'
        }
      },
      getItemSummary: (item) => item.text || 'Navigation Item'
    },
    backgroundColor: {
      type: 'select',
      label: 'Hintergrundfarbe',
      options: backgroundColorOptions()
    },
    textColor: {
      type: 'select',
      label: 'Textfarbe',
      options: textColorOptions()
    },
    logoColor: {
      type: 'select',
      label: 'Logo-Farbe',
      options: textColorOptions()
    },
    padding: {
      type: 'select',
      label: 'Innenabstand',
      options: [
        { label: 'Klein', value: 'sm' },
        { label: 'Mittel', value: 'md' },
        { label: 'Groß', value: 'lg' },
        { label: 'Sehr groß', value: 'xl' }
      ]
    },
    sticky: {
      type: 'radio',
      label: 'Klebrig (Sticky)',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    showLogo: {
      type: 'radio',
      label: 'Logo anzeigen',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    showNavigation: {
      type: 'radio',
      label: 'Navigation anzeigen',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    showThemeToggle: {
      type: 'radio',
      label: 'Theme Toggle anzeigen',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    align: {
      type: 'select',
      label: 'Ausrichtung',
      options: [
        { label: 'Links', value: 'left' },
        { label: 'Zentriert', value: 'center' },
        { label: 'Rechts', value: 'right' },
        { label: 'Zwischen (Logo links, Nav rechts)', value: 'between' }
      ]
    }
  },
  defaultProps: {
    logoText: 'Your Brand',
    logoUrl: '',
    navigation: [
      { text: 'Home', url: '#home' },
      { text: 'About', url: '#about' },
      { text: 'Contact', url: '#contact' }
    ],
    backgroundColor: 'white',
    textColor: 'gray-900',
    logoColor: 'blue-600',
    padding: 'md',
    sticky: false,
    showLogo: true,
    showNavigation: true,
    showThemeToggle: false,
    align: 'center'
  },
  render: HeaderComponent
};
